import React, { useState, useEffect } from 'react';
import { 
  Search, Book, Video, MessageCircle, ExternalLink, ChevronRight, 
  PlayCircle, FileText, HelpCircle, Plus, Trash2, Edit2, Save, 
  ArrowLeft, Upload, Loader2, Play, Settings, Shield, Clock, X
} from 'lucide-react';
import { collection, query, where, onSnapshot, orderBy, 
  addDoc, updateDoc, deleteDoc, doc, serverTimestamp,
  Timestamp } from '../firebase';
import { db, auth } from '../firebase';
import { HelpCategory, HelpArticle } from '../types';
import { notify } from '../services/notify';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const getLocalAuthToken = () => localStorage.getItem('app_auth_token');
const isLocalSession = () => Boolean(getLocalAuthToken());

const localApiRequest = async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
  const response = await fetch(path, {
    ...init,
    headers: {
      Authorization: `Bearer ${getLocalAuthToken()}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.error || `Request failed (${response.status})`);
  return payload as T;
};

const ICON_MAP: Record<string, React.ReactNode> = {
  'PlayCircle': <PlayCircle className="w-5 h-5" />,
  'FileText': <FileText className="w-5 h-5" />,
  'Book': <Book className="w-5 h-5" />,
  'MessageCircle': <MessageCircle className="w-5 h-5" />,
  'Video': <Video className="w-5 h-5" />,
  'HelpCircle': <HelpCircle className="w-5 h-5" />,
  'Shield': <Shield className="w-5 h-5" />,
  'Settings': <Settings className="w-5 h-5" />,
};

interface HelpViewProps {
  orgId: string;
  language?: string;
}

export default function HelpView({ orgId, language = 'en' }: HelpViewProps) {
  const [categories, setCategories] = useState<HelpCategory[]>([]);
  const [articles, setArticles] = useState<HelpArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Navigation State
  const [selectedCategory, setSelectedCategory] = useState<HelpCategory | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<HelpArticle | null>(null);
  
  // Modal/Form State
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<HelpCategory | null>(null);
  const [editingArticle, setEditingArticle] = useState<HelpArticle | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);

  // Form State
  const [categoryForm, setCategoryForm] = useState({
    title: '',
    icon: 'Book',
    description: '',
    order: 0
  });

  const [articleForm, setArticleForm] = useState({
    title: '',
    categoryId: '',
    content: '',
    type: 'Article' as 'Article' | 'Video' | 'Training',
    videoUrl: '',
    attachmentUrl: '',
    duration: '',
    order: 0
  });

  useEffect(() => {
    if (!orgId) return;

    if (isLocalSession()) {
      let cancelled = false;
      setLoading(true);
      void Promise.all([
        localApiRequest<HelpCategory[]>(`/api/organizations/${orgId}/help-categories`),
        localApiRequest<HelpArticle[]>(`/api/organizations/${orgId}/help-articles`),
      ])
        .then(([loadedCategories, loadedArticles]) => {
          if (cancelled) return;
          setCategories(loadedCategories);
          setArticles(loadedArticles);
        })
        .catch(error => {
          console.error('Local API error loading help content:', error);
          if (!cancelled) notify.error(error instanceof Error ? error.message : 'Failed to load help content');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => { cancelled = true; };
    }

    // Fetch Categories
    const categoriesQuery = query(
      collection(db, 'help_categories'),
      where('orgId', '==', orgId),
      orderBy('order', 'asc')
    );

    const unsubCategories = onSnapshot(categoriesQuery, (snapshot) => {
      const cats = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as HelpCategory));
      setCategories(cats);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching categories:", error);
      setLoading(false);
    });

    // Fetch Articles
    const articlesQuery = query(
      collection(db, 'help_articles'),
      where('orgId', '==', orgId),
      orderBy('order', 'asc')
    );

    const unsubArticles = onSnapshot(articlesQuery, (snapshot) => {
      const arts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as HelpArticle));
      setArticles(arts);
    }, (error) => {
      console.error("Error fetching articles:", error);
    });

    return () => {
      unsubCategories();
      unsubArticles();
    };
  }, [orgId]);

  const handleSeedDefaultContent = async () => {
    if (!orgId) return;
    setIsSeeding(true);
    try {
      if (isLocalSession()) {
        const seeded = await localApiRequest<{ categories: HelpCategory[]; articles: HelpArticle[] }>(
          `/api/organizations/${orgId}/help/seed`,
          { method: 'POST' }
        );
        setCategories(seeded.categories);
        setArticles(seeded.articles);
      } else {
        const defaultCategories = [
          { title: 'Getting Started', icon: 'PlayCircle', description: 'Everything you need to know to get started with the platform.', order: 0 },
          { title: 'Invoices & Billing', icon: 'FileText', description: 'Managing your invoices, payments, and billing details.', order: 1 },
          { title: 'Account Settings', icon: 'Settings', description: 'Managing user profiles, roles, and organization settings.', order: 2 },
        ];

        for (const cat of defaultCategories) {
          const catRef = await addDoc(collection(db, 'help_categories'), {
            ...cat,
            orgId,
            createdAt: serverTimestamp()
          });

          if (cat.title === 'Getting Started') {
            await addDoc(collection(db, 'help_articles'), {
              title: 'Welcome to the Platform',
              categoryId: catRef.id,
              content: 'Welcome! This guide will walk you through the basic features of our platform.\n\n### 1. Dashboard Overview\nYour dashboard provides a high-level view of your business metrics.\n\n### 2. Navigation\nUse the sidebar to access different modules like Invoices, POS, Inventory, and settings.',
              type: 'Article',
              videoUrl: '',
              attachmentUrl: '',
              duration: '',
              order: 0,
              orgId,
              createdAt: serverTimestamp()
            });
            await addDoc(collection(db, 'help_articles'), {
              title: 'Platform Walkthrough',
              categoryId: catRef.id,
              content: 'A quick 5-minute video walkthrough covering all core features.',
              type: 'Video',
              videoUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', // Just a placeholder
              attachmentUrl: '',
              duration: '5:20',
              order: 1,
              orgId,
              createdAt: serverTimestamp()
            });
          }
        }
      }
      notify.confirm('Default help content populated successfully');
    } catch (error) {
      console.error('Error seeding content:', error);
      notify.error('Failed to populate default content');
    } finally {
      setIsSeeding(false);
    }
  };

  const handleSaveCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId) return;
    setIsSaving(true);
    try {
      if (isLocalSession()) {
        const endpoint = editingCategory
          ? `/api/organizations/${orgId}/help-categories/${editingCategory.id}`
          : `/api/organizations/${orgId}/help-categories`;
        const saved = await localApiRequest<HelpCategory>(endpoint, {
          method: editingCategory ? 'PATCH' : 'POST',
          body: JSON.stringify(categoryForm)
        });
        setCategories(current => editingCategory
          ? current.map(category => category.id === saved.id ? saved : category)
          : [...current, saved].sort((left, right) => left.order - right.order || left.title.localeCompare(right.title))
        );
        setSelectedCategory(current => current?.id === saved.id ? saved : current);
      } else if (editingCategory) {
        await updateDoc(doc(db, 'help_categories', editingCategory.id!), {
          ...categoryForm,
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, 'help_categories'), {
          ...categoryForm,
          orgId,
          createdAt: serverTimestamp()
        });
      }
      notify.confirm(editingCategory ? 'Category updated successfully' : 'Category created successfully');
      setShowCategoryModal(false);
      setEditingCategory(null);
    } catch (error) {
      console.error('Error saving category:', error);
      notify.error('Failed to save category');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveArticle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId) return;
    setIsSaving(true);
    try {
      if (isLocalSession()) {
        const endpoint = editingArticle
          ? `/api/organizations/${orgId}/help-articles/${editingArticle.id}`
          : `/api/organizations/${orgId}/help-articles`;
        const saved = await localApiRequest<HelpArticle>(endpoint, {
          method: editingArticle ? 'PATCH' : 'POST',
          body: JSON.stringify(articleForm)
        });
        setArticles(current => editingArticle
          ? current.map(article => article.id === saved.id ? saved : article)
          : [...current, saved].sort((left, right) => left.order - right.order || left.title.localeCompare(right.title))
        );
        setSelectedArticle(current => current?.id === saved.id ? saved : current);
      } else if (editingArticle) {
        await updateDoc(doc(db, 'help_articles', editingArticle.id!), {
          ...articleForm,
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, 'help_articles'), {
          ...articleForm,
          orgId,
          createdAt: serverTimestamp()
        });
      }
      notify.confirm(editingArticle ? 'Article updated successfully' : 'Article created successfully');
      setShowArticleModal(false);
      setEditingArticle(null);
    } catch (error) {
      console.error('Error saving article:', error);
      notify.error('Failed to save article');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!confirm('Are you sure you want to delete this category? All articles will remain orphan.')) return;
    try {
      if (isLocalSession()) {
        await localApiRequest(`/api/organizations/${orgId}/help-categories/${id}`, { method: 'DELETE' });
        setCategories(current => current.filter(category => category.id !== id));
        setArticles(current => current.map(article => article.categoryId === id ? { ...article, categoryId: '' } : article));
        if (selectedCategory?.id === id) {
          setSelectedCategory(null);
          setSelectedArticle(null);
        }
      } else {
        await deleteDoc(doc(db, 'help_categories', id));
      }
      notify.confirm('Category deleted');
    } catch (error) {
      console.error('Error deleting category:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to delete category');
    }
  };

  const handleDeleteArticle = async (id: string) => {
    if (!confirm('Are you sure you want to delete this article?')) return;
    try {
      if (isLocalSession()) {
        await localApiRequest(`/api/organizations/${orgId}/help-articles/${id}`, { method: 'DELETE' });
        setArticles(current => current.filter(article => article.id !== id));
      } else {
        await deleteDoc(doc(db, 'help_articles', id));
      }
      notify.confirm('Article deleted');
      if (selectedArticle?.id === id) setSelectedArticle(null);
    } catch (error) {
      console.error('Error deleting article:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to delete article');
    }
  };

  const filteredArticles = articles.filter(art => 
    art.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    art.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getArticlesForCategory = (catId: string) => {
    return articles.filter(art => art.categoryId === catId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin opacity-20" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-12">
      {/* Header & Search */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-6 border-b border-black/10 pb-12">
        <div className="flex-1 space-y-4">
          <div className="flex items-center gap-4">
            <h1 className="text-4xl font-bold tracking-tight italic serif">Help & Training</h1>
            <button 
              onClick={() => setIsAdmin(!isAdmin)}
              className={cn(
                "p-2 rounded-full transition-all border-2",
                isAdmin ? "bg-black text-white border-black" : "hover:bg-black/5 border-transparent"
              )}
              title="Admin Mode"
            >
              <Settings className="w-5 h-5" />
            </button>
          </div>
          <p className="opacity-60 max-w-xl italic serif">
            Browse our documentation, video tutorials, and training materials developed to help you and your clients master the platform.
          </p>
        </div>
        <div className="w-full md:w-96 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 opacity-30" />
          <input 
            type="text"
            placeholder="Search keywords..."
            className="w-full pl-12 pr-4 py-3 bg-white border-2 border-black rounded-xl outline-none focus:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Breadcrumbs */}
      {(selectedCategory || selectedArticle) && (
        <nav className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest opacity-40">
          <button onClick={() => { setSelectedCategory(null); setSelectedArticle(null); }} className="hover:opacity-100">Help Home</button>
          {selectedCategory && (
            <>
              <ChevronRight className="w-4 h-4" />
              <button 
                onClick={() => { setSelectedArticle(null); }} 
                className={cn("hover:opacity-100", !selectedArticle && "opacity-100 text-black")}
              >
                {selectedCategory.title}
              </button>
            </>
          )}
          {selectedArticle && (
            <>
              <ChevronRight className="w-4 h-4" />
              <span className="opacity-100 text-black truncate max-w-[200px]">{selectedArticle.title}</span>
            </>
          )}
        </nav>
      )}

      {/* Main Content Area */}
      {!selectedCategory && !selectedArticle ? (
        <div className="space-y-12">
          {/* Category Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.length === 0 && isAdmin && (
              <div className="col-span-full py-12 text-center bg-white border-2 border-black border-dashed rounded-3xl space-y-6">
                <div className="w-20 h-20 bg-black/5 rounded-full flex items-center justify-center mx-auto">
                  <Book className="w-10 h-10 opacity-20" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">No Help Content Yet</h3>
                  <p className="opacity-60 italic serif mt-2 max-w-sm mx-auto">Click below to generate some starting categories and articles, or create your own from scratch.</p>
                </div>
                <button 
                  onClick={handleSeedDefaultContent}
                  disabled={isSeeding}
                  className="bg-black text-white px-8 py-3 rounded-full font-bold inline-flex items-center gap-2 hover:scale-105 transition-all shadow-lg mx-auto"
                >
                  {isSeeding ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Populating...</>
                  ) : (
                    <><PlayCircle className="w-5 h-5" /> Seed Default Content</>
                  )}
                </button>
              </div>
            )}
            
            {categories.map((category) => (
              <div 
                key={category.id} 
                onClick={() => setSelectedCategory(category)}
                className="bg-white border-2 border-black p-8 rounded-3xl shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 transition-all cursor-pointer flex flex-col justify-between group h-full"
              >
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="p-4 bg-black text-white rounded-2xl group-hover:rotate-6 transition-transform">
                      {ICON_MAP[category.icon] || <Book className="w-5 h-5" />}
                    </div>
                    {isAdmin && (
                      <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                        <button 
                          onClick={() => {
                            setEditingCategory(category);
                            setCategoryForm({
                              title: category.title,
                              icon: category.icon,
                              description: category.description || '',
                              order: category.order || 0
                            });
                            setShowCategoryModal(true);
                          }}
                          className="p-2 hover:bg-black/5 rounded-full"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDeleteCategory(category.id!)} className="p-2 hover:bg-red-50 text-red-500 rounded-full">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{category.title}</h3>
                    <p className="text-sm opacity-60 line-clamp-2">{category.description || "Browse articles and tutorials in this category."}</p>
                  </div>
                </div>
                <div className="pt-6 mt-6 border-t border-black/5">
                  <div className="flex items-center justify-between text-xs font-bold uppercase tracking-widest">
                    <span>{`${getArticlesForCategory(category.id!).length} resources`}</span>
                    <ArrowLeft className="w-4 h-4 rotate-180" />
                  </div>
                </div>
              </div>
            ))}
            {isAdmin && (
              <button 
                onClick={() => {
                  setEditingCategory(null);
                  setCategoryForm({ title: '', icon: 'Book', description: '', order: categories.length });
                  setShowCategoryModal(true);
                }}
                className="border-4 border-dashed border-black/10 p-8 rounded-3xl hover:bg-black/5 transition-all flex flex-col items-center justify-center gap-4 group"
              >
                <div className="w-16 h-16 rounded-full bg-black/5 flex items-center justify-center group-hover:bg-black group-hover:text-white transition-all">
                  <Plus className="w-8 h-8" />
                </div>
                <span className="font-bold uppercase tracking-widest text-sm">Add Category</span>
              </button>
            )}
          </div>

          {/* Video Section (Featured/Training) */}
          <div className="space-y-8 bg-black text-white p-12 rounded-[3rem]">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold italic serif">Training Videos</h2>
                <p className="opacity-50 text-sm mt-2">Essential walk-throughs for new users.</p>
              </div>
              {isAdmin && (
                <button 
                  onClick={() => {
                    setEditingArticle(null);
                    setArticleForm({
                      title: '',
                      categoryId: categories[0]?.id || '',
                      content: '',
                      type: 'Video',
                      videoUrl: '',
                      attachmentUrl: '',
                      duration: '',
                      order: 0
                    });
                    setShowArticleModal(true);
                  }}
                  className="flex items-center gap-2 bg-white text-black px-6 py-3 rounded-full font-bold hover:scale-105 transition-all text-sm"
                >
                  <Plus className="w-4 h-4" /> Add Video
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.filter(a => a.type === 'Video' || a.type === 'Training').slice(0, 3).map((video) => (
                <div 
                  key={video.id} 
                  onClick={() => setSelectedArticle(video)}
                  className="group cursor-pointer space-y-4"
                >
                  <div className="relative aspect-video rounded-2xl overflow-hidden border-2 border-white/20 group-hover:border-white/50 transition-all shadow-xl">
                    <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-60" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 bg-white text-black rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-2xl">
                        <Play className="w-6 h-6 fill-current ml-1" />
                      </div>
                    </div>
                    {video.duration && (
                      <div className="absolute bottom-4 right-4 bg-black/80 px-2 py-1 rounded text-[10px] font-bold">
                        {video.duration}
                      </div>
                    )}
                  </div>
                  <div>
                    <h4 className="font-bold text-lg group-hover:underline">{video.title}</h4>
                    <p className="text-xs opacity-50 uppercase tracking-widest mt-1">
                      {categories.find(c => c.id === video.categoryId)?.title}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : selectedCategory && !selectedArticle ? (
        <div className="space-y-12">
          {/* Category Header */}
          <div className="flex flex-col md:flex-row items-end justify-between gap-6">
            <div className="space-y-4">
              <h1 className="text-4xl font-bold tracking-tight">{selectedCategory.title}</h1>
              <p className="opacity-60 italic serif max-w-2xl">{selectedCategory.description}</p>
            </div>
            {isAdmin && (
              <button 
                onClick={() => {
                  setEditingArticle(null);
                  setArticleForm({
                    title: '',
                    categoryId: selectedCategory.id!,
                    content: '',
                    type: 'Article',
                    videoUrl: '',
                    attachmentUrl: '',
                    duration: '',
                    order: getArticlesForCategory(selectedCategory.id!).length 
                  });
                  setShowArticleModal(true);
                }}
                className="flex items-center gap-2 bg-black text-white px-8 py-4 rounded-full font-bold hover:scale-105 transition-all shadow-lg"
              >
                <Plus className="w-5 h-5" /> Add Resource
              </button>
            )}
          </div>

          {/* Resources List */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {getArticlesForCategory(selectedCategory.id!).map((article) => (
              <div 
                key={article.id}
                onClick={() => setSelectedArticle(article)}
                className="bg-white border border-black/10 p-6 rounded-2xl hover:border-black/30 hover:shadow-lg transition-all cursor-pointer group flex items-start justify-between"
              >
                <div className="flex gap-4">
                  <div className="p-3 bg-black/5 rounded-xl group-hover:bg-black group-hover:text-white transition-all">
                    {article.type === 'Video' ? <Video className="w-5 h-5" /> : 
                     article.type === 'Training' ? <HelpCircle className="w-5 h-5" /> : 
                     <FileText className="w-5 h-5" />}
                  </div>
                  <div>
                    <h3 className="font-bold text-lg group-hover:underline">{article.title}</h3>
                    <div className="flex items-center gap-4 mt-2 text-[10px] uppercase font-bold tracking-widest opacity-40">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {article.type}</span>
                      {article.duration && <span>{article.duration}</span>}
                    </div>
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                    <button 
                      onClick={() => {
                        setEditingArticle(article);
                        setArticleForm({
                          title: article.title,
                          categoryId: article.categoryId,
                          content: article.content,
                          type: article.type,
                          videoUrl: article.videoUrl || '',
                          attachmentUrl: article.attachmentUrl || '',
                          duration: article.duration || '',
                          order: article.order || 0
                        });
                        setShowArticleModal(true);
                      }}
                      className="p-2 hover:bg-black/5 rounded-full"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDeleteArticle(article.id!)} className="p-2 hover:bg-red-50 text-red-500 rounded-full">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            ))}
            {getArticlesForCategory(selectedCategory.id!).length === 0 && (
              <div className="col-span-full py-20 text-center space-y-4">
                <div className="w-20 h-20 bg-black/5 rounded-full flex items-center justify-center mx-auto">
                  <Book className="w-10 h-10 opacity-20" />
                </div>
                <p className="italic serif opacity-40">No resources have been added to this category yet.</p>
              </div>
            )}
          </div>
        </div>
      ) : selectedArticle ? (
        <div className="space-y-12">
          {/* Article Header */}
          <div className="space-y-6">
            <button 
              onClick={() => setSelectedArticle(null)}
              className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest hover:opacity-60 transition-opacity"
            >
              <ArrowLeft className="w-4 h-4" /> Back to {selectedCategory?.title}
            </button>
            <h1 className="text-5xl font-bold leading-tight max-w-4xl">{selectedArticle.title}</h1>
            <div className="flex items-center gap-6 text-sm font-bold uppercase tracking-widest opacity-40">
              <span className="bg-black text-white px-3 py-1 rounded-full text-[10px]">{selectedArticle.type}</span>
              {selectedArticle.duration && <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {selectedArticle.duration}</span>}
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> Published {new Date(selectedArticle.createdAt?.toDate?.() || (selectedArticle.createdAt as unknown as string | number | Date) || Date.now()).toLocaleDateString()}</span>
            </div>
          </div>

          {/* Article Content Area */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-12">
              {/* Media Section */}
              {(selectedArticle.type === 'Video' || selectedArticle.videoUrl) && (
                <div className="aspect-video bg-black rounded-3xl overflow-hidden shadow-2xl border border-black/10">
                  {selectedArticle.videoUrl ? (
                    <iframe 
                      src={String(selectedArticle?.videoUrl || '').replace('watch?v=', 'embed/')} 
                      className="w-full h-full"
                      allowFullScreen
                      title={selectedArticle.title}
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-6 text-white text-center p-12">
                      <Video className="w-16 h-16 opacity-20" />
                      <p className="italic serif opacity-60">This video resource is currently unavailable.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Text Content */}
              <div className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:italic prose-headings:serif prose-p:opacity-80 prose-p:leading-relaxed">
                <div dangerouslySetInnerHTML={{ __html: String(selectedArticle?.content || '').replace(/\n/g, '<br />') }} />
              </div>

              {/* Attachments */}
              {selectedArticle.attachmentUrl && (
                <div className="bg-white border-2 border-black p-8 rounded-3xl flex items-center justify-between group cursor-pointer hover:bg-black hover:text-white transition-all shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:shadow-none hover:translate-x-1 hover:translate-y-1">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-black text-white rounded-xl group-hover:bg-white group-hover:text-black transition-all">
                      <Upload className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="font-bold italic serif text-lg">Training Resources</h4>
                      <p className="text-xs opacity-60 uppercase tracking-widest mt-1">Download additional materials for this session</p>
                    </div>
                  </div>
                  <ExternalLink className="w-6 h-6" />
                </div>
              )}
            </div>

            {/* Sidebar (Related Content) */}
            <div className="space-y-8">
              <div className="bg-white/50 border border-black/10 rounded-3xl p-8 space-y-6">
                <h3 className="text-sm font-bold uppercase tracking-widest opacity-40">Other in {selectedCategory?.title}</h3>
                <nav className="space-y-4">
                  {getArticlesForCategory(selectedArticle.categoryId).filter(a => a.id !== selectedArticle.id).map(art => (
                    <button 
                      key={art.id}
                      onClick={() => setSelectedArticle(art)}
                      className="w-full text-left font-bold text-sm hover:underline flex items-start gap-2 group"
                    >
                      <ChevronRight className="w-4 h-4 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                      <span>{art.title}</span>
                    </button>
                  ))}
                  {getArticlesForCategory(selectedArticle.categoryId).length <= 1 && (
                    <p className="italic serif text-sm opacity-40">No other resources in this category.</p>
                  )}
                </nav>
              </div>

              <div className="bg-black text-white p-8 rounded-3xl space-y-6">
                <h3 className="text-sm font-bold uppercase tracking-widest opacity-50">Need more help?</h3>
                <p className="text-sm italic serif leading-loose opacity-70">
                  If this article didn't clear your doubts, feel free to reach out to our dedicated support team.
                </p>
                <button className="w-full py-4 bg-white text-black font-bold rounded-full hover:scale-105 transition-all text-sm flex items-center justify-center gap-2">
                  <MessageCircle className="w-4 h-4" /> Contact Support
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Category Modal */}
      {showCategoryModal && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4 overflow-y-auto overscroll-contain"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setShowCategoryModal(false);
          }}
        >
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain rounded-3xl shadow-2xl p-8 space-y-8 animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold italic serif">
                {editingCategory ? 'Edit Category' : 'New Category'}
              </h2>
              <button onClick={() => setShowCategoryModal(false)} className="p-2 hover:bg-black/5 rounded-full"><X className="w-6 h-6" /></button>
            </div>
            <form onSubmit={handleSaveCategory} className="space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Category Title</label>
                <input 
                  type="text" 
                  required
                  className="w-full p-4 bg-white border-2 border-black rounded-xl outline-none focus:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all font-bold"
                  value={categoryForm.title}
                  onChange={e => setCategoryForm({...categoryForm, title: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Icon</label>
                <select 
                  className="w-full p-4 bg-white border-2 border-black rounded-xl outline-none"
                  value={categoryForm.icon}
                  onChange={e => setCategoryForm({...categoryForm, icon: e.target.value})}
                >
                  {Object.keys(ICON_MAP).map(icon => <option key={icon} value={icon}>{icon}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Description</label>
                <textarea 
                  className="w-full p-4 bg-white border-2 border-black rounded-xl outline-none min-h-[100px]"
                  value={categoryForm.description}
                  onChange={e => setCategoryForm({...categoryForm, description: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Display Order</label>
                <input 
                  type="number" 
                  className="w-full p-4 bg-white border-2 border-black rounded-xl outline-none"
                  value={categoryForm.order}
                  onChange={e => setCategoryForm({...categoryForm, order: parseInt(e.target.value)})}
                />
              </div>
              <div className="pt-4 flex gap-4">
                <button 
                  type="submit" 
                  disabled={isSaving}
                  className="flex-1 bg-black text-white py-4 rounded-full font-bold hover:scale-105 transition-all flex items-center justify-center gap-2"
                >
                  {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                  {editingCategory ? 'Update Category' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Article Modal */}
      {showArticleModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white w-full max-w-2xl rounded-3xl shadow-2xl p-8 space-y-8 animate-in zoom-in-95 duration-200 my-auto">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold italic serif">
                {editingArticle ? 'Edit Resource' : 'New Resource'}
              </h2>
              <button onClick={() => setShowArticleModal(false)} className="p-2 hover:bg-black/5 rounded-full"><X className="w-6 h-6" /></button>
            </div>
            <form onSubmit={handleSaveArticle} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Resource Title</label>
                  <input 
                    type="text" 
                    required
                    className="w-full p-4 bg-white border-2 border-black rounded-xl outline-none font-bold"
                    value={articleForm.title}
                    onChange={e => setArticleForm({...articleForm, title: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Resource Type</label>
                  <select 
                    className="w-full p-4 bg-white border-2 border-black rounded-xl outline-none"
                    value={articleForm.type}
                    onChange={e => setArticleForm({...articleForm, type: e.target.value as any})}
                  >
                    <option value="Article">Article</option>
                    <option value="Video">Video</option>
                    <option value="Training">Training Session</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Category</label>
                <select 
                  required
                  className="w-full p-4 bg-white border-2 border-black rounded-xl outline-none"
                  value={articleForm.categoryId}
                  onChange={e => setArticleForm({...articleForm, categoryId: e.target.value})}
                >
                  <option value="">Select a category...</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Content (Markdown supported)</label>
                <textarea 
                  required
                  className="w-full p-4 bg-white border-2 border-black rounded-xl outline-none min-h-[150px]"
                  value={articleForm.content}
                  onChange={e => setArticleForm({...articleForm, content: e.target.value})}
                />
              </div>

              {(articleForm.type === 'Video' || articleForm.type === 'Training') && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Video URL (YouTube/Vimeo)</label>
                    <input 
                      type="url" 
                      className="w-full p-4 bg-white border-2 border-black rounded-xl outline-none"
                      value={articleForm.videoUrl}
                      onChange={e => setArticleForm({...articleForm, videoUrl: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Duration (e.g. 5:24)</label>
                    <input 
                      type="text" 
                      className="w-full p-4 bg-white border-2 border-black rounded-xl outline-none"
                      value={articleForm.duration}
                      onChange={e => setArticleForm({...articleForm, duration: e.target.value})}
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Resource Attachment URL</label>
                <input 
                  type="url" 
                  className="w-full p-4 bg-white border-2 border-black rounded-xl outline-none"
                  value={articleForm.attachmentUrl}
                  onChange={e => setArticleForm({...articleForm, attachmentUrl: e.target.value})}
                />
              </div>

              <div className="pt-4">
                <button 
                  type="submit" 
                  disabled={isSaving}
                  className="w-full bg-black text-white py-4 rounded-full font-bold hover:scale-105 transition-all flex items-center justify-center gap-2"
                >
                  {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                  {editingArticle ? 'Update Resource' : 'Publish Resource'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
